import React from 'react';
import { Camera, Users, Calendar, CheckCircle, Globe, Music } from 'lucide-react';

const Features = ({ language }) => {
  const content = {
    en: {
      title: 'Powerful Features for Choir Management',
      subtitle: 'Everything you need to run a successful choir',
      features: [
        {
          icon: Camera,
          title: 'Photo Albums',
          description: 'Create stunning photo galleries to showcase your choir performances, events, and memories.',
          details: []
        },
        {
          icon: Users,
          title: 'Member Cadastre',
          description: 'Complete member management system with registration, profiles, and communication tools.',
          details: ['Member profiles', 'Contact management', 'Role assignments', 'Communication tools']
        },
        {
          icon: Calendar,
          title: 'Event Calendar',
          description: 'Schedule and manage rehearsals, concerts, and special events with integrated calendar.',
          details: ['Event scheduling', 'Rehearsal planning', 'Concert management', 'Calendar subscription for members']
        },
        {
          icon: CheckCircle,
          title: 'Attendance Tracking',
          description: 'Track member attendance for rehearsals and events with detailed reporting.',
          details: ['Digital check-in', 'Attendance reports', 'Performance analytics', 'Absence notifications']
        },
        {
          icon: Globe,
          title: 'Multi-Language Support',
          description: 'Reach global audiences with full multi-language support and localization.',
          details: ['Multiple languages', 'Regional settings', 'Cultural adaptations', 'RTL support']
        },
        {
          icon: Music,
          title: 'Public Website',
          description: 'Beautiful public-facing website to promote your choir and attract new members.',
          details: ['Custom themes', 'SEO optimization', 'Mobile responsive', 'Social integration']
        }
      ]
    },
    es: {
      title: 'Características Poderosas para la Gestión Coral',
      subtitle: 'Todo lo que necesitas para dirigir un coro exitoso',
      features: [
        {
          icon: Camera,
          title: 'Álbumes de Fotos',
          description: 'Crea galerías de fotos impresionantes para mostrar las actuaciones, eventos y recuerdos de tu coro.',
          details: []
        },
        {
          icon: Users,
          title: 'Registro de Miembros',
          description: 'Sistema completo de gestión de miembros con registro, perfiles y herramientas de comunicación.',
          details: ['Perfiles de miembros', 'Gestión de contactos', 'Asignación de roles', 'Herramientas de comunicación']
        },
        {
          icon: Calendar,
          title: 'Calendario de Eventos',
          description: 'Programa y gestiona ensayos, conciertos y eventos especiales con calendario integrado.',
          details: ['Programación de eventos', 'Planificación de ensayos', 'Gestión de conciertos', 'Suscripción al calendario para miembros']
        },
        {
          icon: CheckCircle,
          title: 'Seguimiento de Asistencia',
          description: 'Rastrea la asistencia de miembros a ensayos y eventos con informes detallados.',
          details: ['Check-in digital', 'Informes de asistencia', 'Análisis de rendimiento', 'Notificaciones de ausencia']
        },
        {
          icon: Globe,
          title: 'Soporte Multi-idioma',
          description: 'Alcanza audiencias globales con soporte completo multi-idioma y localización.',
          details: ['Múltiples idiomas', 'Configuraciones regionales', 'Adaptaciones culturales', 'Soporte RTL']
        },
        {
          icon: Music,
          title: 'Sitio Web Público',
          description: 'Hermoso sitio web público para promocionar tu coro y atraer nuevos miembros.',
          details: ['Temas personalizados', 'Optimización SEO', 'Responsive móvil', 'Integración social']
        }
      ]
    },
    sv: {
      title: 'Kraftfulla Funktioner för Körhantering',
      subtitle: 'Allt du behöver för att driva en framgångsrik kör',
      features: [
        {
          icon: Camera,
          title: 'Fotoalbum',
          description: 'Skapa fantastiska fotogallerier för att visa upp din körs framträdanden, evenemang och minnen.',
          details: []
        },
        {
          icon: Users,
          title: 'Medlemsregister',
          description: 'Komplett medlemshanteringssystem med registrering, profiler och kommunikationsverktyg.',
          details: ['Medlemsprofiler', 'Kontakthantering', 'Rolltilldelningar', 'Kommunikationsverktyg']
        },
        {
          icon: Calendar,
          title: 'Evenemangskalender',
          description: 'Schemalägg och hantera repetitioner, konserter och speciella evenemang med integrerad kalender.',
          details: ['Evenemangsschemaläggning', 'Repetitionsplanering', 'Konserthantering', 'Kalenderprenumeration för medlemmar']
        },
        {
          icon: CheckCircle,
          title: 'Närvarospårning',
          description: 'Spåra medlemsnärvaro för repetitioner och evenemang med detaljerad rapportering.',
          details: ['Digital incheckning', 'Närvarorapporter', 'Prestationsanalys', 'Frånvaronotiser']
        },
        {
          icon: Globe,
          title: 'Flerspråkigt Stöd',
          description: 'Nå globala målgrupper med fullständigt flerspråkigt stöd och lokalisering.',
          details: ['Flera språk', 'Regionala inställningar', 'Kulturella anpassningar', 'RTL-stöd']
        },
        {
          icon: Music,
          title: 'Offentlig Webbplats',
          description: 'Vacker offentlig webbplats för att marknadsföra din kör och locka nya medlemmar.',
          details: ['Anpassade teman', 'SEO-optimering', 'Mobilresponsiv', 'Social integration']
        }
      ]
    }
  };

  const t = content[language];

  return (
    <div className="container">
      <div style={{ textAlign: 'center', color: 'white', padding: '2rem 0' }}>
        <h1>{t.title}</h1>
        <p style={{ fontSize: '1.2rem', opacity: 0.9 }}>{t.subtitle}</p>
      </div>
      
      <div className="features-grid">
        {t.features.map((feature, index) => (
          <div key={index} className="feature-card">
            <feature.icon size={48} className="feature-icon" />
            <h3>{feature.title}</h3>
            <p>{feature.description}</p>
            <ul style={{ textAlign: 'left', marginTop: '1rem' }}>
              {feature.details.map((detail, i) => (
                <li key={i} style={{ margin: '0.5rem 0' }}>✓ {detail}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Features;